/**
 * Schedule View — the appointment note.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE. The node-tests execute it verbatim (they strip `export ` and eval
 * the source), so a single `import` breaks the suite. No Vue, no store, no DOM.
 *
 * Open Dental's "Appointment Note" (`appt.Note`) is free text the office types in Edit Appointment,
 * and OD prints it inside the appointment block. /schedule-view/day has always carried it on every
 * appointment; the page used to leave it out on purpose and shows it everywhere since 2026-10-24
 * (the grid block, the checklist card, the phone card, the Table row and its printed sheet).
 *
 * Two jobs live here, both pure:
 *   • WHAT the note says — `appointmentNote`: the one definition of "does this visit have a note",
 *     so no surface can draw "null", an empty strip, or a carriage return;
 *   • HOW MUCH of it a block has room for — `noteLinesThatFit`: whole lines only.
 */

/**
 * The height of one line of note on a grid block, in CSS px. A WHOLE number on purpose: the block
 * clamps the note to `floor(room / line)` lines, and that is only exact when a line is exactly this
 * tall. `.sv-appt__note` in AppointmentBlock.vue sets `line-height` to this same number (a node-test
 * holds the two together).
 */
export const NOTE_LINE_PX = 14;

/**
 * The browser reports a flex item's height as a float ("27.999999px" for a room that is really
 * 28px). Without this slack that noise would cost a whole line; a hundredth of a pixel is far below
 * anything that could clip a glyph.
 */
export const NOTE_FIT_TOLERANCE_PX = 0.01;

/**
 * The visit's note, ready to draw — or '' when there is none.
 *
 * '' for: no appointment, no `Note`, null, empty, whitespace-only (spaces, tabs, blank lines, NBSP)
 * and anything that is not text. Otherwise the text as written, with only the clean-up a page needs:
 *   • Open Dental's CRLF (and a lone CR) become one LF, so a carriage return never reaches the page
 *     and `white-space: pre-line` breaks lines exactly where the office did;
 *   • trailing spaces on a line are dropped;
 *   • a run of blank lines becomes ONE blank line — three empty lines would spend three of the few
 *     lines a block has room for on nothing;
 *   • the whole is trimmed.
 *
 * ⛔ Never touches the words themselves (no truncation, no case change): the note is the office's.
 */
export function appointmentNote(appt) {
  const raw = appt && appt.Note;
  if (typeof raw !== 'string') return '';
  return raw
    .replace(/\r\n?/g, '\n')
    .replace(/[^\S\n]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * The note on ONE line — "line one / line two" — for a screen reader's label, which has no line
 * breaks. '' for no note.
 */
export function noteOneLine(note) {
  if (typeof note !== 'string') return '';
  return note.trim().replace(/\s*\n\s*/g, ' / ');
}

/**
 * How many WHOLE lines of `linePx` fit in `availablePx` of room.
 *
 * This is what makes a block show "as many whole lines as it has room for, never a half-clipped
 * one" (the way Open Dental draws it): the block measures the room left under its name, icons and
 * procedures — which depends on how those wrap, so no formula could know it — and asks this for the
 * `-webkit-line-clamp`. Rounds DOWN, always: a clamp one line too many is exactly the half-line this
 * exists to prevent.
 *
 * 0 for a room that cannot be measured (NaN, a string, negative) or a line height that is not a
 * positive number — nothing is drawn rather than guessed.
 */
export function noteLinesThatFit(availablePx, linePx = NOTE_LINE_PX) {
  const room = typeof availablePx === 'number' ? availablePx : NaN;
  const line = typeof linePx === 'number' ? linePx : NaN;
  if (!Number.isFinite(room) || !Number.isFinite(line) || line <= 0 || room <= 0) return 0;
  return Math.floor((room + NOTE_FIT_TOLERANCE_PX) / line);
}
